import type {
  BuildSessionReportInput,
  SessionReportHealth,
  SessionReportHealthGroup,
  SessionReportSummary,
} from "./types";
import {
  SESSION_REPORT_THRESHOLDS,
  resolveLatestReportStatus,
  resolveMetricsSnapshot,
  resolveSessionReportSupportFlags,
} from "./summary";

const block = (
  supported: boolean,
  status: SessionReportHealthGroup["status"],
  reasons: string[]
): SessionReportHealthGroup => ({
  supported,
  status,
  reasons,
});

const inactive = (): SessionReportHealthGroup => block(true, "inactive", []);
const unsupported = (): SessionReportHealthGroup => block(false, "unsupported", []);
const healthy = (): SessionReportHealthGroup => block(true, "healthy", []);

export const deriveSessionReportHealth = (
  input: BuildSessionReportInput,
  summary: SessionReportSummary
): SessionReportHealth => {
  const metricsSnapshot = resolveMetricsSnapshot(input);
  const support = resolveSessionReportSupportFlags(metricsSnapshot);
  const latestStatus = resolveLatestReportStatus(input);
  const inactiveSession = input.auditState.startedAt === null;
  const transcriptReasons: string[] = [];

  if (
    summary.p95FirstPartialLatencyMs !== null &&
    summary.p95FirstPartialLatencyMs >= SESSION_REPORT_THRESHOLDS.partialLatencyMs
  ) {
    transcriptReasons.push("Partial transcript latency exceeded the threshold.");
  }
  if (
    summary.p95FinalLatencyMs !== null &&
    summary.p95FinalLatencyMs >= SESSION_REPORT_THRESHOLDS.finalLatencyMs
  ) {
    transcriptReasons.push("Final transcript latency exceeded the threshold.");
  }
  if (
    summary.p95StabilizationLatencyMs !== null &&
    summary.p95StabilizationLatencyMs >= SESSION_REPORT_THRESHOLDS.finalLatencyMs
  ) {
    transcriptReasons.push("Transcript stabilization latency exceeded the threshold.");
  }
  if ((summary.finalTranscriptCount ?? 0) === 0 && (summary.partialTranscriptCount ?? 0) > 0) {
    transcriptReasons.push("Partial transcripts were observed without any final transcript.");
  }

  const sessionReasons: string[] = [];
  if (summary.outcome === "failed") {
    sessionReasons.push("Fatal session errors were observed.");
  } else if (
    input.auditState.incidents.some(
      (incident) => incident.type === "error" || incident.type === "fatal_error"
    )
  ) {
    sessionReasons.push("Session errors were observed.");
  }

  const connectionReasons: string[] = [];
  if ((summary.reconnectCount ?? 0) > 0) {
    connectionReasons.push("Reconnect activity was observed.");
  }
  if ((summary.offlineDurationMs ?? 0) > 0) {
    connectionReasons.push("Browser offline time was observed.");
  }
  if ((summary.flapCount ?? 0) > 0) {
    connectionReasons.push("Browser network flaps were observed.");
  }
  if (latestStatus?.connectionState === "disconnected") {
    connectionReasons.push("Current connection state is disconnected.");
  }

  const recoveryReasons: string[] = [];
  if ((summary.reconnectCount ?? 0) > 0) {
    recoveryReasons.push("Recovery was required.");
  }
  if ((summary.p95RecoveryDurationMs ?? 0) >= SESSION_REPORT_THRESHOLDS.recoveryDurationMs) {
    recoveryReasons.push("Recovery latency exceeded the threshold.");
  }
  if (summary.outcome === "degraded_unrecovered" || summary.outcome === "failed") {
    recoveryReasons.push("Recovery did not stabilize the session.");
  }

  const bufferingReasons: string[] = [];
  if ((summary.peakBufferedAudioBytes ?? 0) > 0) {
    bufferingReasons.push("Buffered audio was observed.");
  }
  if (summary.bufferStallCount !== null && summary.bufferStallCount > 0) {
    bufferingReasons.push("Buffered audio stalled.");
  }
  if (latestStatus?.drainExitReason === "stalled" || summary.drainExitReason === "stalled") {
    bufferingReasons.push("Audio drain stalled.");
  }
  if (
    latestStatus?.finishDeliveryState === "timed_out" ||
    summary.finishDeliveryState === "timed_out"
  ) {
    bufferingReasons.push("Buffered finish delivery timed out.");
  }
  if (summary.outcome === "degraded_unrecovered") {
    bufferingReasons.push("Buffered audio was not fully drained.");
  }

  const browserReasons: string[] = [];
  if ((summary.offlineDurationMs ?? 0) > 0) {
    browserReasons.push("Browser offline time was observed.");
  }
  if ((summary.flapCount ?? 0) > 0) {
    browserReasons.push("Browser network flaps were observed.");
  }

  const audioReasons: string[] = [];
  if ((summary.clippingEventCount ?? 0) > 0) {
    audioReasons.push("Clipping was observed.");
  }
  if ((summary.audioCallbackGapCount ?? 0) > 0) {
    audioReasons.push("Audio callback gaps were observed.");
  }

  return {
    transcriptUi: !support.transcriptUi
      ? unsupported()
      : inactiveSession
        ? inactive()
        : transcriptReasons.length > 0
          ? block(true, "degraded", transcriptReasons)
          : healthy(),
    session: !support.session
      ? unsupported()
      : inactiveSession
        ? inactive()
        : sessionReasons.some((reason) => reason.indexOf("Fatal") >= 0)
          ? block(true, "critical", sessionReasons)
          : sessionReasons.length > 0
            ? block(true, "degraded", sessionReasons)
            : healthy(),
    connection: !support.connection
      ? unsupported()
      : inactiveSession
        ? inactive()
        : connectionReasons.some((reason) => reason.indexOf("disconnected") >= 0)
          ? block(true, "critical", connectionReasons)
          : connectionReasons.length > 0
            ? block(true, "degraded", connectionReasons)
            : healthy(),
    recovery: !support.recovery
      ? unsupported()
      : inactiveSession
        ? inactive()
        : recoveryReasons.some((reason) => reason.indexOf("did not stabilize") >= 0)
          ? block(true, "critical", recoveryReasons)
          : recoveryReasons.length > 0
            ? block(true, "degraded", recoveryReasons)
            : healthy(),
    buffering: !support.buffering
      ? unsupported()
      : inactiveSession
        ? inactive()
        : bufferingReasons.some((reason) => reason.indexOf("stalled") >= 0 || reason.indexOf("timed out") >= 0 || reason.indexOf("not fully drained") >= 0)
          ? block(true, "critical", bufferingReasons)
          : bufferingReasons.length > 0
            ? block(true, "degraded", bufferingReasons)
            : healthy(),
    browserNetwork: !support.browserNetwork
      ? unsupported()
      : inactiveSession
        ? inactive()
        : browserReasons.length > 0
          ? block(true, "degraded", browserReasons)
          : healthy(),
    audioCapture: !support.audioCapture
      ? unsupported()
      : inactiveSession && audioReasons.length === 0
        ? inactive()
        : audioReasons.length > 0
          ? block(true, "degraded", audioReasons)
          : healthy(),
  };
};
